"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.visClassId = exports.visClassUri = exports.visClassUrl = exports.insightDisplayFormUsage = exports.insightReduceItems = exports.insightModifyItems = exports.insightSetBuckets = exports.insightSetFilters = exports.insightSetSorts = exports.insightSetProperties = exports.insightIsLocked = exports.insightUpdatedBy = exports.insightUpdated = exports.insightCreatedBy = exports.insightCreated = exports.insightUri = exports.insightId = exports.insightRef = exports.insightSummary = exports.insightTags = exports.insightTitle = exports.insightVisualizationUrl = exports.insightProperties = exports.insightTotals = exports.insightSorts = exports.insightFilters = exports.insightHasDataDefined = exports.insightHasAttributes = exports.insightAttributes = exports.insightHasMeasures = exports.insightMeasures = exports.insightItems = exports.insightBuckets = exports.insightBucket = exports.isInsight = void 0;
var tslib_1 = require("tslib");
// (C) 2019-2022 GoodData Corporation
var isEmpty_1 = tslib_1.__importDefault(require("lodash/isEmpty"));
var intersection_1 = tslib_1.__importDefault(require("lodash/intersection"));
var sort_1 = require("../execution/base/sort");
var buckets_1 = require("../execution/buckets");
var filter_1 = require("../execution/filter");
var measure_1 = require("../execution/measure");
var attribute_1 = require("../execution/attribute");
var bucketArray_1 = require("../execution/buckets/bucketArray");
var ts_invariant_1 = tslib_1.__importDefault(require("ts-invariant"));
var identity_1 = tslib_1.__importDefault(require("lodash/identity"));
var objRef_1 = require("../objRef");
var flatMap_1 = tslib_1.__importDefault(require("lodash/flatMap"));
var uniqBy_1 = tslib_1.__importDefault(require("lodash/uniqBy"));
//
// Type guards
//
/**
 * Type guard checking whether the provided object is an Insight.
 *
 * @public
 */
function isInsight(obj) {
    return !isEmpty_1.default(obj) && obj.insight !== undefined;
}
exports.isInsight = isInsight;
//
// Functions
//
/**
 * Finds bucket matching the provided predicate in an insight.
 *
 * This function also provides convenience to find bucket by its local identifier - if you pass predicate as
 * string the function will automatically create idMatchBucket predicate.
 *
 * @param insight - insight to work with
 * @param idOrFun - local identifier or bucket predicate
 * @returns undefined if none match
 * @public
 */
function insightBucket(insight, idOrFun) {
    if (idOrFun === void 0) { idOrFun = buckets_1.anyBucket; }
    ts_invariant_1.default(insight, "insight must be specified");
    return bucketArray_1.bucketsFind(insight.insight.buckets, idOrFun);
}
exports.insightBucket = insightBucket;
/**
 * Gets buckets for the insight. If ids are provided, then only returns buckets matching the ids.
 *
 * @param insight - insight to work with
 * @param ids - local identifiers of buckets
 * @returns empty list if none match
 * @public
 */
function insightBuckets(insight) {
    var ids = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        ids[_i - 1] = arguments[_i];
    }
    ts_invariant_1.default(insight, "insight must be specified");
    if (isEmpty_1.default(ids)) {
        return insight.insight.buckets;
    }
    return bucketArray_1.bucketsById.apply(void 0, tslib_1.__spreadArrays([insight.insight.buckets], ids));
}
exports.insightBuckets = insightBuckets;
/**
 * Gets all attributes and measures used in the provided insight.
 *
 * @param insight - insight to work with
 * @returns empty if none
 * @public
 */
function insightItems(insight) {
    ts_invariant_1.default(insight, "insight must be specified");
    return bucketArray_1.bucketsItems(insight.insight.buckets);
}
exports.insightItems = insightItems;
/**
 * Gets all measures used in the provided insight.
 *
 * @param insight - insight to work with
 * @param measurePredicate - predicate to select measures satisfying some conditions
 * @returns empty if none
 * @public
 */
function insightMeasures(insight, measurePredicate) {
    if (measurePredicate === void 0) { measurePredicate = measure_1.anyMeasure; }
    ts_invariant_1.default(insight, "insight must be specified");
    return bucketArray_1.bucketsMeasures(insight.insight.buckets, measurePredicate);
}
exports.insightMeasures = insightMeasures;
/**
 * Tests whether insight uses any measures.
 *
 * @param insight - insight to test
 * @returns true if any measures, false if not
 * @public
 */
function insightHasMeasures(insight) {
    ts_invariant_1.default(insight, "insight must be specified");
    return insightMeasures(insight).length > 0;
}
exports.insightHasMeasures = insightHasMeasures;
/**
 * Gets all attributes used in the provided insight
 *
 * @param insight - insight to work with
 * @param attributePredicate - predicate to select attributes satisfying some conditions
 * @returns empty if none
 * @public
 */
function insightAttributes(insight, attributePredicate) {
    if (attributePredicate === void 0) { attributePredicate = attribute_1.anyAttribute; }
    ts_invariant_1.default(insight, "insight must be specified");
    return bucketArray_1.bucketsAttributes(insight.insight.buckets, attributePredicate);
}
exports.insightAttributes = insightAttributes;
/**
 * Tests whether insight uses any attributes
 *
 * @param insight - insight to test
 * @returns true if any measures, false if not
 * @public
 */
function insightHasAttributes(insight) {
    ts_invariant_1.default(insight, "insight must be specified");
    return insightAttributes(insight).length > 0;
}
exports.insightHasAttributes = insightHasAttributes;
/**
 * Tests whether insight contains valid definition of data to visualise - meaning at least one attribute or
 * one measure is defined in the insight.
 *
 * @param insight - insight to test
 * @returns true if at least one measure or attribute, false if none
 * @public
 */
function insightHasDataDefined(insight) {
    ts_invariant_1.default(insight, "insight must be specified");
    return (insight.insight.buckets.length > 0 && (insightHasMeasures(insight) || insightHasAttributes(insight)));
}
exports.insightHasDataDefined = insightHasDataDefined;
/**
 * Gets filters used in an insight.
 *
 * @param insight - insight to work with
 * @public
 */
function insightFilters(insight) {
    ts_invariant_1.default(insight, "insight must be specified");
    return insight.insight.filters;
}
exports.insightFilters = insightFilters;
/**
 * Gets sorting defined in the insight.
 *
 * Note: this function ensures that only sorts working on top of attributes and measures defined in the
 * insight will be returned. Any invalid entries will be stripped.
 *
 * @param insight - insight to get sorts from
 * @returns array of valid sorts
 * @public
 */
function insightSorts(insight) {
    ts_invariant_1.default(insight, "insight must be specified");
    var attributeIds = insightAttributes(insight).map(attribute_1.attributeLocalId);
    var measureIds = insightMeasures(insight).map(measure_1.measureLocalId);
    function contains(arr1, arr2) {
        return intersection_1.default(arr1, arr2).length === arr2.length;
    }
    return insight.insight.sorts.filter(function (s) {
        var entities = sort_1.sortEntityIds(s);
        return (contains(attributeIds, entities.attributeIdentifiers) &&
            contains(measureIds, entities.measureIdentifiers));
    });
}
exports.insightSorts = insightSorts;
/**
 * Gets all totals defined in the insight
 *
 * @param insight - insight to get totals from
 * @returns empty if none
 * @public
 */
function insightTotals(insight) {
    ts_invariant_1.default(insight, "insight must be specified");
    return bucketArray_1.bucketsTotals(insight.insight.buckets);
}
exports.insightTotals = insightTotals;
/**
 * Gets visualization properties of an insight.
 *
 * @param insight - insight to get vis properties for
 * @returns empty object is no properties
 * @public
 */
function insightProperties(insight) {
    ts_invariant_1.default(insight, "insight must be specified");
    return insight.insight.properties;
}
exports.insightProperties = insightProperties;
/**
 * Gets URL of visualization that should be used to render this insight. This is a link to the location
 * where the visualization assets are stored and where they should be loaded and linked from.
 *
 * Note: at the moment, the SDK supports only compile-time linkage; for this the visualization URL
 * is in format "local:visName" (as in "local:bar" for BarChart)
 *
 * @param insight - insight to get visualization URL from
 * @alpha
 */
function insightVisualizationUrl(insight) {
    ts_invariant_1.default(insight, "insight to get vis class URI from must be specified");
    return insight.insight.visualizationUrl;
}
exports.insightVisualizationUrl = insightVisualizationUrl;
/**
 * Gets the insight title
 *
 * @param insight - insight to get title of
 * @returns the insight title
 * @public
 */
function insightTitle(insight) {
    ts_invariant_1.default(insight, "insight to get title from must be specified");
    return insight.insight.title;
}
exports.insightTitle = insightTitle;
/**
 * Gets the insights tags from the tagging system
 *
 * @param insight - insight to get the tags of
 * @returns the insight tags or aan empty array if none are specified
 * @public
 */
function insightTags(insight) {
    var _a;
    ts_invariant_1.default(insight, "insight must be specified");
    return (_a = insight.insight.tags) !== null && _a !== void 0 ? _a : [];
}
exports.insightTags = insightTags;
/**
 * Gets the insights summary
 *
 * @param insight - insight to get the summary of
 * @returns the insight summary or an empty string if is not specified
 * @public
 */
function insightSummary(insight) {
    var _a;
    ts_invariant_1.default(insight, "insight must be specified");
    return (_a = insight.insight.summary) !== null && _a !== void 0 ? _a : "";
}
exports.insightSummary = insightSummary;
/**
 * Gets opaque reference to the insight.
 *
 * @param insight - insight to get ref of
 * @public
 */
function insightRef(insight) {
    ts_invariant_1.default(insight, "insight to get ref of must be specified");
    return insight.insight.ref;
}
exports.insightRef = insightRef;
/**
 * Gets the insight id
 *
 * @param insight - insight to get id of
 * @returns the insight id
 * @public
 */
function insightId(insight) {
    ts_invariant_1.default(insight, "insight to get id of must be specified");
    return insight.insight.identifier;
}
exports.insightId = insightId;
/**
 * Gets the insight uri
 *
 * @param insight - insight to get uri of
 * @returns the insight uri
 * @public
 */
function insightUri(insight) {
    ts_invariant_1.default(insight, "insight to get uri of must be specified");
    return insight.insight.uri;
}
exports.insightUri = insightUri;
/**
 * Gets the date when the insight was created
 *
 * @param insight - insight
 * @returns string - YYYY-MM-DD HH:mm:ss
 * @public
 */
function insightCreated(insight) {
    ts_invariant_1.default(insight, "insight must be specified");
    return insight.insight.created;
}
exports.insightCreated = insightCreated;
/**
 * Gets the user that created the insight
 *
 * @param insight - insight
 * @returns string
 * @public
 */
function insightCreatedBy(insight) {
    ts_invariant_1.default(insight, "insight must be specified");
    return insight.insight.createdBy;
}
exports.insightCreatedBy = insightCreatedBy;
/**
 * Gets the date of the last insight update
 *
 * @param insight - insight
 * @returns string - YYYY-MM-DD HH:mm:ss
 * @public
 */
function insightUpdated(insight) {
    ts_invariant_1.default(insight, "insight must be specified");
    return insight.insight.updated;
}
exports.insightUpdated = insightUpdated;
/**
 * Gets the user that last updated the insight
 *
 * @param insight - insight
 * @returns string
 * @public
 */
function insightUpdatedBy(insight) {
    ts_invariant_1.default(insight, "insight must be specified");
    return insight.insight.updatedBy;
}
exports.insightUpdatedBy = insightUpdatedBy;
/**
 * Checks if insight is locked
 *
 * @param insight - insight
 * @returns boolean
 * @public
 */
function insightIsLocked(insight) {
    ts_invariant_1.default(insight, "insight must be specified");
    return insight.insight.isLocked || false;
}
exports.insightIsLocked = insightIsLocked;
/**
 * Gets a new insight that 'inherits' all data from the provided insight but has different properties. New
 * properties will be used in the new insight as-is, no merging with existing properties.
 *
 * @param insight - insight to work with
 * @param properties - new properties to have on the new insight
 * @returns always new instance
 * @public
 */
function insightSetProperties(insight, properties) {
    if (properties === void 0) { properties = {}; }
    ts_invariant_1.default(insight, "insight must be specified");
    return {
        insight: tslib_1.__assign(tslib_1.__assign({}, insight.insight), { properties: properties }),
    };
}
exports.insightSetProperties = insightSetProperties;
/**
 * Gets a new insight that 'inherits' all data from the provided insight but has different sorts. New
 * sorts will be used in the new insight as-is, no merging with existing sorts.
 *
 * @param insight - insight to work with
 * @param sorts - new sorts to apply
 * @returns always new instance
 * @public
 */
function insightSetSorts(insight, sorts) {
    if (sorts === void 0) { sorts = []; }
    ts_invariant_1.default(insight, "insight must be specified");
    return {
        insight: tslib_1.__assign(tslib_1.__assign({}, insight.insight), { sorts: sorts }),
    };
}
exports.insightSetSorts = insightSetSorts;
/**
 * Gets a new insight that 'inherits' all data from the provided insight but has different filters. New
 * filters will be used in the new insight as-is, no merging with existing filters.
 *
 * @param insight - insight to work with
 * @param filters - new filters to apply
 * @returns always new instance
 * @public
 */
function insightSetFilters(insight, filters) {
    if (filters === void 0) { filters = []; }
    ts_invariant_1.default(insight, "insight must be specified");
    return {
        insight: tslib_1.__assign(tslib_1.__assign({}, insight.insight), { filters: filters }),
    };
}
exports.insightSetFilters = insightSetFilters;
/**
 * Gets a new insight that 'inherits' all data from the provided insight but has different buckets. New
 * buckets will be used in the new insight as-is, no merging with existing buckets.
 *
 * @param insight - insight to work with
 * @param buckets - new buckets to apply
 * @returns always new instance
 * @public
 */
function insightSetBuckets(insight, buckets) {
    if (buckets === void 0) { buckets = []; }
    ts_invariant_1.default(insight, "insight must be specified");
    return {
        insight: tslib_1.__assign(tslib_1.__assign({}, insight.insight), { buckets: buckets }),
    };
}
exports.insightSetBuckets = insightSetBuckets;
/**
 * Creates a new insight with modified bucket items (retrieved by applying the modifications function to each bucketItem in the insight).
 *
 * Note: the bucket item modification function SHOULD NOT modify bucket item's localId.
 * The localId MAY be used to reference the item from other places in the insight (for example from sorts).
 * Changing the item localId has potential to break the insight: as-is this function does not concern itself with changing the references.
 *
 * @param insight - insight to use as template for the new insight
 * @param modifications - modifications to apply to the bucket items
 * @returns always new instance
 * @public
 */
function insightModifyItems(insight, modifications) {
    if (modifications === void 0) { modifications = identity_1.default; }
    ts_invariant_1.default(insight, "insight must be specified");
    var buckets = insightBuckets(insight);
    return {
        insight: tslib_1.__assign(tslib_1.__assign({}, insight.insight), { buckets: bucketArray_1.bucketsModifyItem(buckets, modifications) }),
    };
}
exports.insightModifyItems = insightModifyItems;
/**
 * Creates a new insight with reduced bucket items (retrieved by applying the modifications function).
 *
 * Note: the bucket item modification function SHOULD NOT modify bucket item's localId.
 * The localId MAY be used to reference the item from other places in the insight (for example from sorts).
 * Changing the item localId has potential to break the insight: as-is this function does not concern itself with changing the references.
 *
 * @param insight - insight to use as template for the new insight
 * @param reducer - reduce function to apply to the bucket items
 * @returns always new instance
 * @public
 */
function insightReduceItems(insight, reducer) {
    if (reducer === void 0) { reducer = identity_1.default; }
    ts_invariant_1.default(insight, "insight must be specified");
    var buckets = insightBuckets(insight);
    return {
        insight: tslib_1.__assign(tslib_1.__assign({}, insight.insight), { buckets: bucketArray_1.bucketsReduceItem(buckets, reducer) }),
    };
}
exports.insightReduceItems = insightReduceItems;
/**
 * Gets references to all display forms used by the insight. The display forms may be used for slicing or dicing the
 * data, for filtering the entire insight or for filtering just some measures.
 *
 * @param insight - insight to get the display form usage from
 * @public
 */
function insightDisplayFormUsage(insight) {
    ts_invariant_1.default(insight, "insight must be specified");
    return {
        inAttributes: uniqBy_1.default(insightAttributes(insight).map(attribute_1.attributeDisplayFormRef), objRef_1.serializeObjRef),
        inFilters: uniqBy_1.default(insightFilters(insight)
            .filter(filter_1.isAttributeFilter)
            .map(function (attributeFilter) { return filter_1.filterObjRef(attributeFilter); }), objRef_1.serializeObjRef),
        inMeasureFilters: uniqBy_1.default(flatMap_1.default(insightMeasures(insight), function (measure) {
            var _a;
            var filters = (_a = measure_1.measureFilters(measure)) !== null && _a !== void 0 ? _a : [];
            return filters
                .filter(filter_1.isAttributeFilter)
                .map(function (attributeFilter) { return filter_1.filterObjRef(attributeFilter); });
        }), objRef_1.serializeObjRef),
    };
}
exports.insightDisplayFormUsage = insightDisplayFormUsage;
//
// Visualization class functions
//
/**
 * For given visualization class, return URL where the vis assets are stored.
 *
 * @param vc - visualization class
 * @returns never null, never empty
 * @public
 */
function visClassUrl(vc) {
    ts_invariant_1.default(vc, "vis class to get URL from must be specified");
    return vc.visualizationClass.url;
}
exports.visClassUrl = visClassUrl;
/**
 * For given visualization class, return its URI.
 *
 * @param vc - visualization class
 * @returns never null, never empty
 * @public
 */
function visClassUri(vc) {
    ts_invariant_1.default(vc, "vis class to get URI from must be specified");
    return vc.visualizationClass.uri;
}
exports.visClassUri = visClassUri;
/**
 * For given visualization class, return its identifier.
 *
 * @param vc - visualization class
 * @returns never null, never empty
 * @public
 */
function visClassId(vc) {
    ts_invariant_1.default(vc, "vis class to get URI from must be specified");
    return vc.visualizationClass.identifier;
}
exports.visClassId = visClassId;
//# sourceMappingURL=index.js.map